import {
  AppRoute,
  type PageAdminEvaluationsData,
  QueryReference,
} from '@agentselly/immodossier-shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useBackendQuery } from '~/helper/useBackendQuery';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { formatDateTime } from '@agentselly/ui';
import { useTranslate } from '@tolgee/react';
import { clsx } from 'clsx';
import ReactJson from 'react-json-view';
import { useState } from 'react';
import { useTheme } from '~/context/ThemeContext';
import { useAppParams } from '~/helper/useAppParams';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_ADMIN_EVALUATIONS,
)({
  component: Body,
});

function Body() {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { lang, companyId } = useAppParams();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const currentFilter: QueryFilter = Route.useSearch();
  const query = useBackendQuery(QueryReference.PageAdminEvaluations);

  const [
    dialogContent,
    setDialogContent,
  ] = useState('');
  const [
    dialogTitle,
    setDialogTitle,
  ] = useState('');

  if (query.isLoading) {
    return <LoadingWidget />;
  }

  if (query.isError) {
    return <ErrorWidget error={query.error} />;
  }

  async function updateFilter(filter: QueryFilter) {
    const nextSearch: QueryFilter = {};

    for (const key of [
      'page',
      'showFollowup',
      'showInternal',
      'hideErrors',
      'hideSuccess',
      'wannaSell',
      'appId',
      'showOnlyValiant',
    ]) {
      const value = filter[key];

      if (typeof value === 'undefined') {
        continue;
      }

      if (!value.length) {
        continue;
      }

      nextSearch[key] = value;
    }

    await navigate({
      to: Route.fullPath,
      params: { lang, companyId },
      search: nextSearch as unknown as QueryFilter,
      replace: true,
    });

    await query.refetch();
  }

  const paginationProps: PaginationWidgetProps = {
    pagination: query.data?.pagination,
    resultDate: formatDateTime(query.data?.resultTimestamp),
    updateFilter: updateFilter,
  };

  let parsedModalJson: object | string | null = null;
  if (dialogContent) {
    try {
      parsedModalJson = JSON.parse(dialogContent);
    } catch {
      parsedModalJson = dialogContent;
    }
  }

  return (
    <div className="v3-stack pb-8">
      {/* Raw Data Modal Dialog */}
      {dialogContent ? (
        <div
          className="v3-modal-backdrop"
          onClick={() => setDialogContent('')}
        >
          <div
            className="v3-modal max-w-4xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center pb-3 border-b border-[var(--v3-bd)]">
              <h3 className="v3-modal-title">
                {dialogTitle || t('admin_evaluations_raw_data', 'Rohdaten')}
              </h3>
              <button
                className="v3-btn sm"
                onClick={() => setDialogContent('')}
              >
                {t('common_close', 'Schliessen')}
              </button>
            </div>
            <div className="overflow-auto p-2 flex-1 max-h-[65vh]">
              {typeof parsedModalJson === 'object' &&
              parsedModalJson !== null ? (
                <ReactJson
                  src={parsedModalJson as object}
                  name={false}
                  collapsed={1}
                  theme={isDark ? 'monokai' : 'rjv-default'}
                  style={{
                    backgroundColor: 'transparent',
                    fontSize: '13px',
                  }}
                />
              ) : (
                <pre className="v3-pre">{dialogContent}</pre>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Header */}
      <div className="v3-head">
        <h1>{t('admin_evaluations_title', 'Bewertungen')}</h1>
        <p>
          {t(
            'admin_evaluations_subtitle',
            'Übersicht und Filter aller durchgeführten Immobilienbewertungen',
          )}
        </p>
      </div>

      {/* Filter Card */}
      <div className="v3-card">
        <h2 className="text-base font-bold text-[var(--v3-tx)]">
          {t('admin_evaluations_filter_title', 'Filter & Suche')}
        </h2>

        <div className="v3-fil-grid">
          <div>
            <label
              className="block text-xs font-semibold text-[var(--v3-mu)] mb-1"
              htmlFor="wannaSell"
            >
              {t('admin_evaluations_reason', 'Bewertungsgrund')}
            </label>
            <select
              id="wannaSell"
              className="v3-inp"
              onChange={(event) =>
                updateFilter({
                  wannaSell: event.target.value,
                })
              }
              defaultValue={currentFilter.wannaSell || ''}
            >
              <option value="">{t('common_all', 'Alle Gründe')}</option>
              <option value="sell12">SQL (sell12)</option>
              <option value="sell24">MQL (sell24)</option>
              <option value="sell">Unklar (sell)</option>
              <option value="buy">Buy</option>
              <option value="value">Value</option>
            </select>
          </div>

          <div>
            <label
              className="block text-xs font-semibold text-[var(--v3-mu)] mb-1"
              htmlFor="appId"
            >
              {t('admin_evaluations_appid', 'AppId')}
            </label>
            <select
              id="appId"
              className="v3-inp"
              onChange={(event) =>
                updateFilter({
                  appId: event.target.value,
                })
              }
              defaultValue={currentFilter.appId || ''}
            >
              <option value="">{t('common_all', 'Alle AppIds')}</option>
              <option value="en6fcr">en6fcr</option>
              <option value="dca7b5">dca7b5</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-[var(--v3-bd)]">
          <label
            className="v3-ckb"
            htmlFor="showInternal"
          >
            <input
              id="showInternal"
              type="checkbox"
              onChange={(event) =>
                updateFilter({
                  showInternal: event.target.checked ? 'true' : '',
                })
              }
              defaultChecked={Boolean(currentFilter.showInternal)}
            />
            <span>
              {t('admin_evaluations_show_internal', 'Interne Bewertungen')}
            </span>
          </label>

          <label
            className="v3-ckb"
            htmlFor="showOnlyValiant"
          >
            <input
              id="showOnlyValiant"
              type="checkbox"
              onChange={(event) =>
                updateFilter({
                  showOnlyValiant: event.target.checked ? 'true' : '',
                })
              }
              defaultChecked={Boolean(currentFilter.showOnlyValiant)}
            />
            <span>{t('admin_evaluations_show_valiant', 'Nur Valiant')}</span>
          </label>

          <label
            className="v3-ckb"
            htmlFor="hideErrors"
          >
            <input
              id="hideErrors"
              type="checkbox"
              onChange={(event) =>
                updateFilter({
                  hideErrors: event.target.checked ? '' : 'true',
                })
              }
              defaultChecked={!currentFilter.hideErrors}
            />
            <span>{t('admin_evaluations_show_errors', 'Fehler anzeigen')}</span>
          </label>

          <label
            className="v3-ckb"
            htmlFor="hideSuccess"
          >
            <input
              id="hideSuccess"
              type="checkbox"
              onChange={(event) =>
                updateFilter({
                  hideSuccess: event.target.checked ? '' : 'true',
                })
              }
              defaultChecked={!currentFilter.hideSuccess}
            />
            <span>
              {t('admin_evaluations_show_success', 'Erfolgreiche anzeigen')}
            </span>
          </label>
        </div>
      </div>

      <PaginationWidget {...paginationProps} />

      {/* Evaluations List */}
      <div className="v3-stack">
        <h2 className="text-base font-bold text-[var(--v3-tx)]">
          {t('admin_evaluations_results_title', 'Ergebnisse')}
        </h2>

        {query.data?.evaluations?.length === 0 ? (
          <div className="v3-card text-center py-8 text-[var(--v3-mu)]">
            {t(
              'admin_evaluations_empty',
              'Keine Bewertungen für diese Filterkriterien gefunden.',
            )}
          </div>
        ) : null}

        {query.data?.evaluations?.filter(Boolean)?.map((evaluation, index) => {
          const isOk = evaluation.isSuccessful && !evaluation.hasError;
          const isBad = evaluation.hasError && !evaluation.isSuccessful;
          const statusClass = isOk ? 's-ok' : isBad ? 's-bad' : 's-warn';

          return (
            <div
              key={index}
              className={clsx('v3-ev', statusClass)}
            >
              {/* Header row */}
              <div className="v3-ev-top">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {isOk ? (
                      <span className="v3-chip c-ok">Erfolg</span>
                    ) : isBad ? (
                      <span className="v3-chip c-bad">Fehler</span>
                    ) : (
                      <span className="v3-chip c-warn">Warnung</span>
                    )}

                    {evaluation.valiantDeviceId ? (
                      <span className="v3-chip c-pri">Valiant</span>
                    ) : null}

                    {evaluation.isInternal ? (
                      <span className="v3-chip c-info">Intern</span>
                    ) : null}

                    {evaluation.isFollowup ? (
                      <span className="v3-chip c-neu">Followup</span>
                    ) : null}

                    <span className="text-xs text-[var(--v3-mu)] font-mono">
                      {formatDateTime(evaluation.timestamp)}
                    </span>
                  </div>

                  <div className="v3-ev-title">
                    {evaluation.address ||
                      t('admin_evaluations_no_address', 'Ohne Adresse')}
                  </div>
                </div>

                <span className="text-xs font-mono text-[var(--v3-mu)] break-all">
                  {evaluation.sessionId}
                </span>
              </div>

              {/* Contact information */}
              {[
                evaluation.contactName,
                evaluation.contactEmail,
                evaluation.contactPhone,
              ].some(Boolean) ? (
                <div className="flex flex-wrap gap-4 text-xs text-[var(--v3-mu)] pt-1 border-t border-[var(--v3-bd)]">
                  {evaluation.contactName ? (
                    <span>
                      <strong className="text-[var(--v3-tx)]">Name:</strong>{' '}
                      {evaluation.contactName}
                    </span>
                  ) : null}
                  {evaluation.contactEmail ? (
                    <span>
                      <strong className="text-[var(--v3-tx)]">E-Mail:</strong>{' '}
                      {evaluation.contactEmail}
                    </span>
                  ) : null}
                  {evaluation.contactPhone ? (
                    <span>
                      <strong className="text-[var(--v3-tx)]">Telefon:</strong>{' '}
                      {evaluation.contactPhone}
                    </span>
                  ) : null}
                </div>
              ) : null}

              {/* Metrics & Parameters */}
              <div className="flex flex-wrap gap-3 text-xs">
                {Object.entries({
                  'IAZI-Bewertung': evaluation.iaziEvaluation,
                  TrustScore: evaluation.trustScore,
                  Bewertungsgrund: evaluation.wannaSell,
                  AppId: evaluation.appId,
                  CalculatorVersion:
                    evaluation.rawCalculatorResult?.calculatorVersion,
                  'UTM Source': evaluation.utmSource,
                  'UTM Medium': evaluation.utmMedium,
                  'UTM Campaign': evaluation.utmCampaign,
                  'Valiant Device ID': evaluation.valiantDeviceId,
                })
                  .filter((it) => Boolean(it[1]))
                  .map(
                    ([
                      key,
                      value,
                    ]) => (
                      <span
                        key={key}
                        className="px-2 py-0.5 rounded bg-[var(--v3-neubg)] text-[var(--v3-tx)] border border-[var(--v3-bd)]"
                      >
                        <strong className="text-[var(--v3-mu)]">{key}:</strong>{' '}
                        {String(value)}
                      </span>
                    ),
                  )}
              </div>

              {/* Links and Raw Data inspection buttons */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-[var(--v3-bd)]">
                <div className="flex flex-wrap gap-2 text-xs font-semibold">
                  {Object.entries({
                    'Resultat-Seite': evaluation.links?.resultpage,
                    'HubSpot Property': evaluation.links?.hubspotProperty,
                    'HubSpot Contact': evaluation.links?.hubspotContact,
                    'Admin-Input': evaluation.links?.adminInput,
                    'Admin-Result': evaluation.links?.adminResult,
                  })
                    .filter((it) => Boolean(it[1]))
                    .map(
                      ([
                        key,
                        value,
                      ]) => (
                        <a
                          key={key}
                          href={value}
                          target="_blank"
                          rel="noreferrer"
                          className="v3-btn sm text-xs"
                        >
                          {key} ↗
                        </a>
                      ),
                    )}
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {Object.entries({
                    UTM: evaluation.rawUTM,
                    CalculatorInput: evaluation.rawCalculatorInput,
                    CalculatorResult: evaluation.rawCalculatorResult,
                    Dossier: evaluation.rawDossier,
                  })
                    .filter((it) => Boolean(it[1]))
                    .map(
                      ([
                        key,
                        value,
                      ]) => (
                        <button
                          key={key}
                          type="button"
                          className="v3-btn sm text-xs"
                          onClick={() => {
                            setDialogTitle(
                              `${key} (${
                                evaluation.address || evaluation.sessionId
                              })`,
                            );
                            setDialogContent(JSON.stringify(value, null, 2));
                          }}
                        >
                          {key}
                        </button>
                      ),
                    )}
                </div>
              </div>

              {/* Error Callout */}
              {evaluation.errorType ? (
                <div className="v3-callout warn">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-xs text-[var(--v3-bad)]">
                      LatestErrorType: {evaluation.errorType}
                      {evaluation.errorData ? (
                        <>
                          {' '}
                          ({evaluation.errorData.source}:{' '}
                          {evaluation.errorData.message})
                        </>
                      ) : null}
                    </p>
                    {evaluation.errorData?.stack ? (
                      <details className="mt-2 text-xs">
                        <summary className="cursor-pointer font-semibold underline">
                          Stack Trace anzeigen
                        </summary>
                        <pre className="v3-pre mt-1 text-[11px]">
                          {evaluation.errorData.stack}
                        </pre>
                      </details>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <PaginationWidget {...paginationProps} />

      {/* Status Legend */}
      <div className="v3-card">
        <h2 className="text-base font-bold text-[var(--v3-tx)]">
          {t('admin_evaluations_legend_title', 'Status-Legende')}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="v3-chip c-ok">Erfolg</span>
            <span className="text-[var(--v3-mu)]">
              Erfolgreich berechnet und aufrufbar
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="v3-chip c-bad">Fehler</span>
            <span className="text-[var(--v3-mu)]">
              Fehler bei Berechnung oder Datenübertrag
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="v3-chip c-warn">Warnung</span>
            <span className="text-[var(--v3-mu)]">
              Teilweise erfolgreich oder Zugriffsbeschränkung
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="v3-chip c-pri">Valiant</span>
            <span className="text-[var(--v3-mu)]">
              Bewertung über Valiant-Partnerkanal
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="v3-chip c-info">Intern</span>
            <span className="text-[var(--v3-mu)]">
              Interne AgentSelly Testbewertung
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="v3-chip c-neu">Followup</span>
            <span className="text-[var(--v3-mu)]">
              Erneute Bewertung im Follow-up Flow
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface QueryFilter extends Record<string, string | string[] | undefined> {
  readonly page?: string;
  readonly showFollowup?: string;
  readonly showInternal?: string;
  readonly hideErrors?: string;
  readonly hideSuccess?: string;
  readonly wannaSell?: string;
  readonly appId?: string;
  readonly showOnlyValiant?: string;
}

function PaginationWidget(props: PaginationWidgetProps) {
  const { t } = useTranslate();

  const page = props.pagination?.page || 0;
  const pageCount = props.pagination?.pageCount || 0;

  return (
    <div className="v3-card py-3">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <PaginationButton
            disabled={page === 1}
            updateFilter={props.updateFilter}
            targetPage={1}
            text={t('admin_evaluations_pagination_first', '« Erste')}
          />
          <PaginationButton
            disabled={page - 1 < 1}
            updateFilter={props.updateFilter}
            targetPage={page - 1}
            text={t('admin_evaluations_pagination_previous', '‹ Zurück')}
          />
        </div>

        <p className="text-center text-xs text-[var(--v3-mu)]">
          <strong className="text-[var(--v3-tx)]">
            {t('admin_evaluations_pagination_text', {
              page: page,
              total: pageCount,
            })}
          </strong>
          <br />
          {t('admin_evaluations_pagination_results', {
            resultCount: props.pagination?.total || 0,
            resultDate: props.resultDate,
          })}
        </p>

        <div className="flex items-center gap-2">
          <PaginationButton
            disabled={page + 1 > pageCount}
            updateFilter={props.updateFilter}
            targetPage={page + 1}
            text={t('admin_evaluations_pagination_next', 'Vor ›')}
          />
          <PaginationButton
            disabled={page === pageCount}
            updateFilter={props.updateFilter}
            targetPage={pageCount}
            text={t('admin_evaluations_pagination_last', 'Letzte »')}
          />
        </div>
      </div>
    </div>
  );
}

interface PaginationWidgetProps {
  readonly pagination: PageAdminEvaluationsData['pagination'] | undefined;
  readonly resultDate: string;
  readonly updateFilter: (filter: QueryFilter) => void;
}

function PaginationButton(props: PaginationButtonProps) {
  return (
    <button
      onClick={() =>
        props.updateFilter({
          page: props.targetPage?.toString() || undefined,
        })
      }
      disabled={props.disabled}
      className="v3-btn sm text-xs"
    >
      {props.text}
    </button>
  );
}

interface PaginationButtonProps {
  readonly disabled: boolean;
  readonly updateFilter: (filter: QueryFilter) => void;
  readonly targetPage: number;
  readonly text: string;
}
